import React, { useState, useMemo, useEffect, useRef, memo } from 'react';
import {
  Box,
  Typography,
  CircularProgress
} from '@mui/material';
import { fetchGcsObject } from '../../services/api_utils';
import { countNoun } from '../../utils/pluralise';
import PreviewTable from '../functions/PreviewTable';

// A table the Decode Agent produced, shown in its chat thread. This component loads the
// rows (live from the stream, or the persisted copy from storage) and draws them with
// PreviewTable, the same grid as every other data preview, including its Excel export.
// Agent tables carry no declared column types, so PreviewTable is given no schema and a
// string that merely looks like a date is shown exactly as the agent wrote it (M66).
function ChatTable({ tableGcsPath, liveTableData }) {
  const [data, setData] = useState(liveTableData || null);
  const [loading, setLoading] = useState(!liveTableData);
  const [error, setError] = useState(null);
  const dataRef = useRef(data);

  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  useEffect(() => {
    let cancelled = false;

    if (liveTableData) {
      setData(liveTableData);
      setError(null);
      setLoading(false);
      return () => {
        cancelled = true;
      };
    }

    const loadTable = async () => {
      if (!tableGcsPath) {
        if (!dataRef.current) {
          setError("No table path provided.");
        }
        setLoading(false);
        return;
      }
      try {
        // Keep rendering any existing live table while the persisted version hydrates.
        if (!dataRef.current) {
          setLoading(true);
        }
        const tableData = await fetchGcsObject(tableGcsPath);
        if (!cancelled) {
          setData(tableData);
          setError(null);
        }
      } catch (err) {
        if (!cancelled && !dataRef.current) {
          setError("Failed to load table data.");
        }
        console.error(err);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    loadTable();
    return () => {
      cancelled = true;
    };
  }, [tableGcsPath, liveTableData]);

  // Every downstream step - column derivation, header building, cell rendering,
  // Excel export - reads each row's keys, so a row that is not a plain object
  // (null, a number, an array) would crash the whole chat surface. The backend
  // rejects such payloads now, but older persisted tables may still carry them:
  // render the well-formed rows and say how many were dropped (M127).
  const { rows: processedData, omittedRows } = useMemo(() => {
    if (!Array.isArray(data)) return { rows: [], omittedRows: 0 };
    const rows = data.filter(row => row !== null && typeof row === 'object' && !Array.isArray(row));
    return { rows, omittedRows: data.length - rows.length };
  }, [data]);

  if (loading && !data) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={2}>
        <CircularProgress size={20} sx={{ mr: 1 }} />
        <Typography variant="body2">Loading table...</Typography>
      </Box>
    );
  }

  if (error || !Array.isArray(processedData) || processedData.length === 0) {
    return (
      <Typography variant="body2" align="center" sx={{ p: 2, color: 'text.secondary' }}>
        {error || (omittedRows > 0 ? `This table contains no well-formed rows (${omittedRows} malformed).` : 'No data available')}
      </Typography>
    );
  }

  // Inline in the thread the grid scrolls within 300px. Its expanded view opens above the
  // chat's own raised layers, as this table's expanded view always has.
  return (
    <Box sx={{ mt: 1 }}>
      <PreviewTable
        data={processedData}
        maxHeight="300px"
        expandedZIndex={(theme) => theme.zIndex.modal + 3}
      />
      {omittedRows > 0 && (
        <Typography variant="caption" sx={{ display: 'block', mt: 1, opacity: 0.7 }}>
          {countNoun(omittedRows, 'malformed row')} not shown
        </Typography>
      )}
    </Box>
  );
}

// Memoised so a re-render of the thread on every streamed delta (a new messages
// array, the 30 s clock tick, a Firestore snapshot) does not re-render the table:
// its two props are the live rows array the reducer keeps by reference and the
// persisted table's path, both of which only change when the table itself does.
export default memo(ChatTable);
